import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Phone, ChevronLeft, ChevronRight } from "lucide-react";
import { IMAGES } from "@/data/site";
import { useT } from "@/i18n/I18nProvider";
import { useSite } from "@/i18n/useSite";
import { Reveal, SectionHeading } from "@/components/Section";
import { ServiceCard } from "@/components/ServiceCard";
import { Testimonials } from "@/components/Testimonials";
import { TalkCta } from "@/components/TalkCta";

const fade = (d = 0) => ({ initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6, delay: d, ease: [0.22, 1, 0.36, 1] } });

const Hero = () => {
  const t = useT();
  const { BRAND } = useSite();
  return (
    <section className="bg-white" data-testid="home-hero">
      <div className="max-w-[1500px] mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-12 items-end gap-6">
        <div className="lg:col-span-6 py-12 lg:py-20">
          <motion.img {...fade(0)} src={BRAND.logo} alt={BRAND.name} className="h-28 sm:h-36 lg:h-44 w-auto -ms-2" data-testid="hero-logo" />
          <motion.h1 {...fade(0.05)} className="mt-8 text-4xl sm:text-5xl lg:text-[60px] font-bold leading-[1.08]">
            {t("Disability and home care.")}<br /><span className="text-[var(--coral)]">{t("Putting you first.")}</span>
          </motion.h1>
          <motion.p {...fade(0.1)} className="mt-7 text-lg text-[var(--body)] leading-relaxed max-w-lg">{BRAND.subtitle}</motion.p>
          <motion.div {...fade(0.2)} className="mt-9 flex flex-wrap gap-3">
            <Link to="/contact" className="btn btn-coral" data-testid="hero-enquire">{t("Enquire About Support")} <ArrowRight size={16} /></Link>
            <a href={`tel:${BRAND.phone.tel}`} className="btn btn-ghost-blue" data-testid="hero-phone"><Phone size={16} /> {BRAND.phone.number}</a>
          </motion.div>
          <motion.ul {...fade(0.3)} className="mt-10 flex flex-wrap gap-x-8 gap-y-2 text-sm font-semibold text-[var(--ink)]" data-testid="hero-metrics">
            <li className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-[var(--coral)]" /> {t("No minimum hours")}</li>
            <li className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-[var(--sun)]" /> {t("Consistent workers")}</li>
            <li className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-[var(--blue)]" /> {t("NDIS Practice Standards")}</li>
          </motion.ul>
        </div>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15 }} className="lg:col-span-6 flex justify-center lg:justify-end">
          <img src={IMAGES.cutout} alt={t("Three smiling people, one using a wheelchair, holding coral hearts")} className="w-full max-w-[720px] object-contain object-bottom mix-blend-multiply" style={{ filter: "brightness(1.12) contrast(1.08)" }} />
        </motion.div>
      </div>
      <div className="h-6 bg-[var(--blue-tint)]" />
    </section>
  );
};

const OurPromise = () => {
  const t = useT();
  const tiles = [
    { v: t("You"), k: t("At the centre of every decision"), t: "tile-blue corner-tr" },
    { v: "8", k: t("Flexible support services"), t: "tile-sun corner-bl" },
    { v: "2 hrs–24/7", k: t("No minimum hours of support"), t: "tile-sky corner-tr" },
    { v: t("1 day"), k: t("Typical response to enquiries"), t: "tile-coral corner-bl" },
  ];
  return (
    <section className="bg-[var(--blue-tint)] py-16 lg:py-24" data-testid="home-statement">
      <div className="container-x">
        <SectionHeading eyebrow={t("Our promise")} title={t("Care built around compassion, respect and reliability")} text={t("We take the time to listen and shape support around your preferences — whether you need help with everyday tasks, personal care or taking part in your community.")} action={<Link to="/about" className="link-u" data-testid="home-about-link">{t("Learn more about us")}</Link>} />
        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="home-tiles">
          {tiles.map((s) => (
            <Reveal key={s.t} className="h-full">
              <div className={`${s.t} h-full p-7 text-center flex flex-col justify-center min-h-[180px]`}>
                <div className="text-3xl sm:text-4xl font-bold leading-none" style={{ color: "inherit" }}>{s.v}</div>
                <div className="mt-3 text-sm sm:text-[15px] opacity-90">{s.k}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const ServicesCarousel = () => {
  const t = useT();
  const { SERVICES } = useSite();
  const ref = useRef(null);
  const scroll = (d) => ref.current?.scrollBy({ left: d * 340, behavior: "smooth" });
  return (
    <section className="py-16 lg:py-24 bg-white" data-testid="home-services">
      <div className="container-x">
        <SectionHeading eyebrow={t("How we can help")} title={t("Support shaped around your everyday life")} text={t("From a few hours a week to around-the-clock care, choose the supports that fit.")} action={<Link to="/services" className="link-u" data-testid="home-all-services">{t("View all services")}</Link>} />
      </div>
      <div className="max-w-[1500px] mx-auto ps-5 sm:ps-6 lg:ps-[max(1.5rem,calc((100vw-1140px)/2))]">
        <div ref={ref} className="scroll-x mt-10 pe-6">
          {SERVICES.map((s, i) => <ServiceCard key={s.slug} s={s} i={i} className="w-[280px] sm:w-[320px]" />)}
        </div>
      </div>
      <div className="mt-6 flex items-center justify-center gap-3">
        <button onClick={() => scroll(-1)} aria-label={t("Previous services")} data-testid="services-prev" className="w-11 h-11 rounded-full bg-[var(--blue-tint)] text-[var(--blue)] flex items-center justify-center hover:bg-[var(--blue)] hover:text-white transition-colors"><ChevronLeft size={20} /></button>
        <button onClick={() => scroll(1)} aria-label={t("Next services")} data-testid="services-next" className="w-11 h-11 rounded-full bg-[var(--blue)] text-white flex items-center justify-center hover:bg-[var(--blue-dark)] transition-colors"><ChevronRight size={20} /></button>
      </div>
    </section>
  );
};

const Approach = () => {
  const t = useT();
  const { STEPS } = useSite();
  return (
    <section className="py-16 lg:py-24 bg-white border-t border-slate-100" data-testid="home-approach">
      <div className="container-x">
        <SectionHeading eyebrow={t("Our approach")} title={t("Good care starts with understanding you")} />
        <ol className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {STEPS.map((st, i) => (
            <Reveal key={st.n} delay={i * 0.07} className="h-full">
              <li className="h-full border-t-4 border-[var(--coral)] pt-5" data-testid={`step-${st.n}`}>
                <span className="text-sm font-semibold text-[var(--coral)]">{t("Step")} {i + 1}</span>
                <h3 className="mt-2 text-xl font-semibold !text-[var(--blue)]">{st.title}</h3>
                <p className="mt-2 text-[15px] text-[var(--body)] leading-relaxed">{st.text}</p>
              </li>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <>
      <Hero />
      <OurPromise />
      <ServicesCarousel />
      <Approach />
      <Testimonials />
      <TalkCta sourcePage="home" />
    </>
  );
}
